// eslint-disable-next-line no-unused-vars
const Types = require('../../../../../jsdoc-types');

const {
  onOnce,
  addDocumentDelegatedListener,
  onDOMContentLoaded,
} = require('../../common/utils/domEvents');
const {
  getDoubleLookPIDWithoutSize,
} = require('../../common/utils/doubleLookUtils');
const { copyStorageItem } = require('../../common/utils/storage');
const {
  BODY_ELEMENTS,
  LIST_NAMES,
  PAGE_ACTIONS,
} = require('../analyticsConst');
const { analyticsEventBus } = require('../events');
const {
  buildItemListForCategory,
  buildItemListForProductTileLocation,
  buildItemListForReferrer,
} = require('../helpers/itemListHelpers');
const {
  cartItemListStorage,
  clickedItemListStorage,
  createItemListAndPosition,
  impressionItemListStorage,
  setFallbackItemList,
} = require('../stores/itemList');

/**
 * When a user clicks on a product card in the save for later section, we get
 * the product ID from the card and update the product list
 * @param {Event} e The event object
 */
function handleSafeForLaterClick(e) {
  const productCard = e.target.closest(BODY_ELEMENTS.sflProductCard);
  const pid = getDoubleLookPIDWithoutSize(productCard?.dataset.productId);
  if (pid) {
    clickedItemListStorage.setItem(
      pid,
      createItemListAndPosition(LIST_NAMES['save-for-later'], -1)
    );
  }
}

/**
 * When a user clicks on a product card in the wishlist section, we get the
 * product ID from the card and update the product list
 * @param {Event} e The event object
 */
function handleWishlistClick(e) {
  const productCard = e.target.closest(BODY_ELEMENTS.wishlistProductCard);
  const pid = getDoubleLookPIDWithoutSize(productCard?.dataset.productId);
  if (pid) {
    clickedItemListStorage.setItem(
      pid,
      createItemListAndPosition(LIST_NAMES.wishlist, -1)
    );
  }
}

/**
 * Begin the tracking for gaInfo related data
 */
function beginTracking() {
  addDocumentDelegatedListener(
    'click',
    BODY_ELEMENTS.sflProductCardLinks,
    handleSafeForLaterClick
  );
  addDocumentDelegatedListener(
    'click',
    BODY_ELEMENTS.wishlistProductCardLinks,
    handleWishlistClick
  );

  onOnce(window, 'beforeunload', () => {
    document.removeEventListener('click', handleSafeForLaterClick);
    document.removeEventListener('click', handleWishlistClick);
  });

  /*
   * ItemList tracking for fallback list
  \* ------------------------------------------------------------------------ */
  setFallbackItemList(buildItemListForReferrer());

  onDOMContentLoaded(() => {
    const page = document.querySelector('.page');
    const isHome = page?.dataset.action === PAGE_ACTIONS.home;
    if (isHome) {
      setFallbackItemList(LIST_NAMES.home);
    }
  });

  analyticsEventBus.subscribe(
    analyticsEventBus.EVENTS.productListViewed,
    /**
     * Handle a new PLP
     * @param {Object} payload - The payload of the event
     * @param {Object} payload.category Category of the related products
     * @param {Object[]} payload.products Products listed
     * @param {Object} payload.products.display Display config for a product
     * @param {jsdocTypes.FullProduct} payload.products.product Products listed
     * @param {Object} payload.products.urls URLS of the product
     */
    ({ category, products = [] } = {}) => {
      if (category && products.length > 0) {
        const list = buildItemListForCategory(category);
        setFallbackItemList(list);
      }
    }
  );

  analyticsEventBus.subscribe(
    analyticsEventBus.EVENTS.searchReturnedResults,
    /**
     * Handle a new PLP
     * @param {Object} payload - The payload of the event
     * @param {Object} payload.category Category of the related products
     * @param {Object[]} payload.products Products listed
     * @param {Object} payload.products.display Display config for a product
     * @param {jsdocTypes.FullProduct} payload.products.product Products listed
     * @param {Object} payload.products.urls URLS of the product
     */
    ({ products = [] } = {}) => {
      if (products.length > 0) {
        const list = LIST_NAMES.search;
        setFallbackItemList(list);
      }
    }
  );

  /*
   * ItemList tracking for viewed products
  \* ------------------------------------------------------------------------ */
  analyticsEventBus.subscribe(
    analyticsEventBus.EVENTS.productImpressionViewed,
    /**
     * Event triggered when a product (usually a tile) is viewed
     * @param {Object} payload The payload of the event
     * @param {Types.Category} [payload.category] If the location is a PLP Grid,
     *  the category of the ProductSearch can be sent
     * @param {string} payload.location The location where the tile is viewed
     * @param {string} payload.position The product position
     * @param {Types.FullProduct} payload.product The just-viewed product
     */
    ({ category, location, position, product }) => {
      const list = buildItemListForProductTileLocation(location, {
        category,
        product,
      });
      impressionItemListStorage.setItem(
        product.id,
        createItemListAndPosition(list, position)
      );
    }
  );

  /*
   * ItemList tracking for clicked products
  \* ------------------------------------------------------------------------ */
  analyticsEventBus.subscribe(
    analyticsEventBus.EVENTS.productTileClicked,
    /**
     * Save the list after clicking a Product Tile.
     * @param {Object} payload The payload of the event
     * @param {Types.Category} [payload.category] If the location is a PLP Grid,
     *  the category of the ProductSearch can be sent
     * @param {string} payload.location The location where the tile is clicked
     * @param {string} [payload.position] The product position when applies
     * @param {Types.FullProduct} payload.product Target product
     */
    (payload) => {
      const { category, location, position, product } = payload;
      const key = product.id;
      const list = buildItemListForProductTileLocation(location, {
        category,
        product,
      });

      if (list) {
        clickedItemListStorage.setItem(
          key,
          createItemListAndPosition(list, position)
        );
      } else {
        copyStorageItem({
          from: impressionItemListStorage,
          to: clickedItemListStorage,
          key,
        });
      }
    },
    {
      synchronous: true,
    }
  );

  /*
   * ItemList tracking for CART items
  \* ------------------------------------------------------------------------ */
  analyticsEventBus.subscribe(
    analyticsEventBus.EVENTS.productAddedToCart,
    ({ list, position, product }) => {
      const itemListAndPosition = createItemListAndPosition(list, position);
      cartItemListStorage.setItem(product.id, itemListAndPosition);
    },
    {
      synchronous: true,
    }
  );
}

module.exports = {
  beginTracking,
};
